/**
 * Le contenu d'un cahier, gardé dans le navigateur.
 *
 * Hedi, le 25 septembre 2026 : *« Garde un cache dans le browser quand tu
 * récupère le contenu d'un ouvrage »*. Le va-et-vient de la V2 — le catalogue,
 * puis un cahier, puis un chapitre, puis retour au cahier pour le suivant —
 * rechargeait tout à chaque pas, alors que **rien de ce qui est demandé ne
 * change entre deux pas** : le sommaire d'un livre acheté est fixe, et les
 * énoncés découpés le sont aussi.
 *
 * La copie est servie **tout de suite**, et la requête part quand même en
 * arrière-plan ; ce qui revient remplace ce qui est à l'écran. Le parent ne
 * voit donc plus d'attente là où il n'y avait rien à apprendre, et ne lit
 * jamais une copie sans que le serveur ait été redemandé.
 *
 * **`sessionStorage` et non `localStorage`**, pour une raison qui n'est pas
 * technique : ce sont les exercices d'un ouvrage acheté, que la règle 8 tient
 * derrière une authentification. Une copie sur le disque de l'appareil
 * survivrait à la déconnexion, ce qui n'est plus « derrière une
 * authentification » ; celle-ci disparaît avec l'onglet, et `oublierTout()` la
 * retire à la déconnexion sans attendre.
 *
 * Il vit dans `ui/` depuis le 5 octobre 2026, **déplacé** depuis `v2/` : les
 * cahiers de l'élève (« Entraînement », `/eleve/entrainement/cahiers`) font le
 * même va-et-vient, et la coquille enfant n'importe ni `parent/` ni `v2/`. Les
 * copies de l'élève et celles du parent ne se mélangent pas — leurs URL
 * diffèrent (`/me/ouvrages` contre `/enfants/{id}/ouvrages`), et la clé est
 * l'URL.
 *
 * La clé est l'URL construite par `buildUrl`, donc exactement celle qui sera
 * appelée : une clé recomposée à la main finit par oublier un paramètre, et
 * deux chapitres différents se répondent alors l'un pour l'autre.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { buildUrl, call, type CallOptions } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { OperationName, Operations } from "@/api/schema";

const PREFIXE = "tuteur.v2.";

/** Au-delà, une copie n'est plus montrée sans avoir été rafraîchie. Deux
 *  heures : le temps d'une soirée de travail, et un ouvrage ne bouge pas. */
const DUREE_DE_VIE = 2 * 60 * 60 * 1000;

/** Un chapitre entier tient largement dessous ; au-dessus, on ne garde rien
 *  plutôt que de remplir le quota avec une seule réponse. */
const TAILLE_MAX = 400_000;

/** Miroir en mémoire : évite de relire et de re-analyser une réponse de
 *  plusieurs dizaines de kilo-octets à chaque rendu. */
const memoire = new Map<string, { pose: number; valeur: unknown }>();

type Enveloppe = { pose: number; valeur: unknown };

function lire(cle: string): unknown | null {
  const vu = memoire.get(cle);
  if (vu) return Date.now() - vu.pose < DUREE_DE_VIE ? vu.valeur : null;
  try {
    const brut = window.sessionStorage.getItem(PREFIXE + cle);
    if (!brut) return null;
    const enveloppe = JSON.parse(brut) as Enveloppe;
    if (Date.now() - enveloppe.pose >= DUREE_DE_VIE) return null;
    memoire.set(cle, enveloppe);
    return enveloppe.valeur;
  } catch {
    /* un stockage refusé ou illisible ramène au comportement sans cache */
    return null;
  }
}

function ecrire(cle: string, valeur: unknown): void {
  const enveloppe: Enveloppe = { pose: Date.now(), valeur };
  memoire.set(cle, enveloppe);
  try {
    const texte = JSON.stringify(enveloppe);
    if (texte.length > TAILLE_MAX) return;
    window.sessionStorage.setItem(PREFIXE + cle, texte);
  } catch {
    // Quota atteint, le plus souvent. On vide ce qui est à nous et on s'arrête
    // là : réessayer en boucle coûterait plus que le cache ne rapporte.
    oublierTout();
  }
}

/** Ce que la composition d'un entraînement rend faux : le compte des exercices
 *  déjà faits, qui figure sur le catalogue comme sur chaque sommaire. On oublie
 *  donc tout ce qui concerne cet enfant, et pas seulement le chapitre touché. */
export function oublierLesOuvrages(childId: string): void {
  oublierCeQuiContient(`/enfants/${encodeURIComponent(childId)}/ouvrages`);
}

/** La même chose pour les cahiers que l'élève feuillette lui-même : composer
 *  sa feuille change le « fait le … » de ses sommaires comme de ses
 *  chapitres. */
export function oublierMesOuvrages(): void {
  oublierCeQuiContient("/me/ouvrages");
}

function oublierCeQuiContient(marque: string): void {
  for (const cle of [...memoire.keys()]) {
    if (cle.includes(marque)) memoire.delete(cle);
  }
  try {
    for (let rang = window.sessionStorage.length - 1; rang >= 0; rang -= 1) {
      const cle = window.sessionStorage.key(rang);
      if (cle?.startsWith(PREFIXE) && cle.includes(marque)) window.sessionStorage.removeItem(cle);
    }
  } catch {
    /* rien à faire : la copie en mémoire est déjà partie */
  }
}

export function oublierTout(): void {
  memoire.clear();
  try {
    for (let rang = window.sessionStorage.length - 1; rang >= 0; rang -= 1) {
      const cle = window.sessionStorage.key(rang);
      if (cle?.startsWith(PREFIXE)) window.sessionStorage.removeItem(cle);
    }
  } catch {
    /* idem */
  }
}

export type Contenu<N extends OperationName> = {
  donnees: Operations[N]["response"] | null;
  erreur: string | null;
  /**
   * Redemander au serveur, sans changer d'adresse.
   *
   * Pour ce qu'un geste rend faux **ailleurs** que dans l'URL : déclarer une
   * compétence vue en classe change la mention de toutes les lignes qui la
   * portent, sur ce chapitre comme sur les autres. L'appelant oublie la copie
   * (`oublierLesOuvrages`) puis rappelle ceci ; sans quoi l'écran afficherait
   * encore « pas encore vu en classe » sous un bouton qui vient de dire le
   * contraire.
   */
  recharger: () => void;
};

/**
 * Le contenu d'un cahier : d'abord la copie s'il y en a une, puis la réponse.
 *
 * L'erreur **n'efface pas** ce qui est affiché : une copie à l'écran et une
 * connexion perdue valent mieux qu'un écran vide, et le parent voit les deux —
 * ce qu'il a, et que la mise à jour a échoué.
 */
export function useContenuDOuvrage<N extends OperationName>(
  operation: N,
  options: CallOptions<N>,
): Contenu<N> {
  const cle = buildUrl(operation, options);
  const [donnees, setDonnees] = useState<Operations[N]["response"] | null>(
    () => (lire(cle) as Operations[N]["response"] | null) ?? null,
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);
  const recharger = useCallback(() => setTour((avant) => avant + 1), []);
  const vue = useRef<string | null>(null);

  useEffect(() => {
    let annule = false;
    // **Un rechargement volontaire ne vide pas l'écran.** Ce qui est affiché
    // est périmé d'une mention, pas faux : le remplacer par une attente ferait
    // clignoter la liste entière pour un mot qui change. Seul un changement
    // d'adresse repart de la copie — et de la copie, non de `null`, sinon le
    // passage d'un chapitre à l'autre remontrerait une attente que le cache
    // rend inutile.
    if (vue.current !== cle) {
      vue.current = cle;
      setDonnees((lire(cle) as Operations[N]["response"] | null) ?? null);
    }
    setErreur(null);

    void (async () => {
      try {
        const frais = await call(operation, options);
        if (annule) return;
        ecrire(cle, frais);
        setDonnees(frais);
      } catch (echec) {
        if (annule) return;
        setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
      }
    })();

    return () => {
      annule = true;
    };
    // `cle` est l'URL exacte de l'appel : elle porte tous les paramètres, donc
    // elle suffit, et `options` — recréé à chaque rendu — relancerait sans fin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, cle, tour]);

  return { donnees, erreur, recharger };
}
